import type { PropsWithChildren } from "react";
import React, { useMemo, useSyncExternalStore } from "react";
import type { IAsyncPartnerConfigurationManager } from "./types";
import { PartnerContext } from "./PartnerContext";

type Props = PropsWithChildren<{
  configManager: IAsyncPartnerConfigurationManager;
}>;

/**
 * Partner Context Provider listens for partner configuration store updates and provides
 * those memoized updates to context consumers.
 */
export const PartnerContextProvider = ({ configManager, children }: Props) => {
  const { subscribe, getClientState, getServerState, loadPartner, getPartnerConfigForPath } =
    configManager;

  const partnerLoadingState = useSyncExternalStore(subscribe, getClientState, getServerState);
  const partnerConfigs = configManager.getPartnerConfigMap();

  const state = useMemo(
    () => ({
      partnerLoadingState,
      loadPartner,
      partnerConfigs,
      getPartnerConfigForPath,
    }),
    [partnerLoadingState, loadPartner, partnerConfigs, getPartnerConfigForPath]
  );

  return <PartnerContext.Provider value={state}>{children}</PartnerContext.Provider>;
};
